import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useEmotionData } from './EmotionData';

const EmotionScatterPlot = ({ artwork }) => {
  const { rawData, loading, error } = useEmotionData(artwork);

  if (loading) {
    return <div className="w-full max-w-2xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-white">Loading emotion data...</div>;
  }

  if (error) {
    return <div className="w-full max-w-2xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-red-400">{error}</div>;
  }

  if (!rawData || rawData.length === 0) {
    return <div className="w-full max-w-2xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-gray-300">No emotion data available for this artwork</div>;
  }

  // Process data for scatter plot
  const processedData = rawData.reduce((acc, curr) => {
    const emotion = (curr.Emotion || curr.emotion || '').toLowerCase();
    const sessionTime = curr.Session_Start_time || curr.session_start_time;
    const duration = curr.Session_Duration || curr.session_duration || 0;
    
    if (!acc[emotion]) {
      acc[emotion] = [];
    }
    
    if (sessionTime) {
      const [day, month, year] = sessionTime.split('/');
      const formattedDate = `${year}-${month}-${day}`;
      acc[emotion].push([
        new Date(formattedDate).getTime(),
        duration,
        curr.camera_id || curr.Camera_ID || 0
      ]);
    }
    return acc;
  }, {});

  // 3-color gradient functions for each emotion
  const getEmotionGradient = (emotion) => {
    const gradients = {
      positive: {
        type: 'radial',
        x: 0.5,
        y: 0.5,
        r: 0.5,
        colorStops: [
          { offset: 0, color: '#10b981' },   // Emerald Green (center)
          { offset: 0.5, color: '#34d399' },  // Bright Emerald
          { offset: 1, color: '#6ee7b7' }     // Light Emerald (edge)
        ]
      },
      neutral: {
        type: 'radial',
        x: 0.5,
        y: 0.5,
        r: 0.5,
        colorStops: [
          { offset: 0, color: '#f59e0b' },   // Amber (center)
          { offset: 0.5, color: '#fbbf24' },  // Bright Amber
          { offset: 1, color: '#fcd34d' }     // Light Amber (edge)
        ]
      },
      negative: {
        type: 'radial',
        x: 0.5,
        y: 0.5,
        r: 0.5,
        colorStops: [
          { offset: 0, color: '#ef4444' },   // Red (center)
          { offset: 0.5, color: '#f87171' },  // Bright Red
          { offset: 1, color: '#fca5a5' }     // Light Red (edge)
        ]
      }
    };
    return gradients[emotion] || '#9E9E9E';
  };

  const emotionGlowColors = {
    positive: "rgba(16, 185, 129, 0.7)",    // Emerald Green glow
    neutral: "rgba(245, 158, 11, 0.7)",     // Amber glow
    negative: "rgba(239, 68, 68, 0.7)"      // Red glow
  };

  const series = Object.keys(processedData).map(emotion => ({
    name: emotion.charAt(0).toUpperCase() + emotion.slice(1),
    type: 'scatter',
    data: processedData[emotion],
    symbolSize: 12,
    itemStyle: {
      color: getEmotionGradient(emotion),
      shadowBlur: 15,
      shadowColor: emotionGlowColors[emotion] || "rgba(158, 158, 158, 0.7)",
      shadowOffsetX: 0,
      shadowOffsetY: 0,
      borderWidth: 2,
      borderColor: '#fff'
    },
    emphasis: {
      focus: 'series',
      itemStyle: {
        shadowBlur: 25,
        shadowColor: emotionGlowColors[emotion] || "rgba(158, 158, 158, 0.9)",
        symbolSize: 18,
        borderWidth: 3
      }
    }
  }));

  const option = {
    title: {
      text: 'Emotion Over Time',
      left: 'center',
      top: '3%',
      textStyle: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#333'
      }
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: 1,
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      padding: [10, 15],
      formatter: function(params) {
        const data = params.data;
        const date = new Date(data[0]).toLocaleDateString();
        const emotionColors = {
          Positive: '#00ffff',
          Neutral: '#FFD700',
          Negative: '#fe75fe'
        };
        const color = emotionColors[params.seriesName] || '#fff';
        return `<div style="font-weight: bold; margin-bottom: 5px; color: ${color};">${params.seriesName}</div>Date: <span style="font-weight: bold;">${date}</span><br/>Duration: <span style="color: ${color}; font-weight: bold;">${data[1].toFixed(2)}s</span><br/>Camera: <span style="font-weight: bold;">${data[2]}</span>`;
      }
    },
    legend: {
      data: series.map(s => s.name),
      bottom: '2%',
      left: 'center',
      itemGap: 25,
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold'
      }
    },
    xAxis: {
      type: 'time',
      name: 'Time',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#666'
      },
      axisLabel: {
        rotate: -45,
        fontSize: 12,
        fontWeight: 'bold',
        color: '#666',
        margin: 10,
        formatter: function(value) {
          return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
          width: 2
        }
      },
      axisTick: {
        lineStyle: {
          color: '#ddd'
        }
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      name: 'Duration (s)',
      nameLocation: 'middle',
      nameGap: 55,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#666'
      },
      axisLabel: {
        formatter: '{value}s',
        fontSize: 13,
        fontWeight: 'bold',
        color: '#666'
      },
      axisLine: {
        lineStyle: {
          color: '#ddd',
          width: 2
        }
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#eee',
          width: 1
        }
      }
    },
    series: series,
    grid: {
      left: '12%',
      right: '8%',
      bottom: '18%',
      top: '15%',
      containLabel: true
    }
  };

  return (
    <div className="w-full max-w-2xl">
      <div className="h-64">
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default EmotionScatterPlot;